import { esNombreAdjuntoBorradorFicha } from './isFichaSeguimientoCompleta';
import { MAPEO_TITULOS_CODIGO } from './bienestarDetalleConstants';
import { COD_SOCIOEMOCIONAL, COD_FICHA_UNICA } from '../../helper';


export const normalizarBienestar = (data) => (Array.isArray(data) ? data[0] : data);

export const normalizarTextoBusqueda = (valor) => String(valor || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();


export const normalizarCodigoModulo = (valor) => {
    const raw = String(valor || '').trim().toUpperCase();
    if (!raw) return null;
    const limpio = raw.replace(/\s+/g, '');
    const matchFase = limpio.match(/^F(\d+)[_\-.]?(\d{1,3})$/);
    if (matchFase) {
        return `F${matchFase[1]}_${String(matchFase[2]).padStart(3, '0')}`;
    }
    const matchTransversal = limpio.match(/^T[_\-.]?(\d+)$/);
    if (matchTransversal) {
        return `T_${matchTransversal[1]}`;
    }
    return limpio;
};


export const extraerModuloAdjunto = (adjunto = {}) => {
    const candidatos = [
        adjunto?.tipo_formulario,
        adjunto?.formulario_codigo,
        adjunto?.codigo_formulario,
        adjunto?.modulo_codigo,
        adjunto?.codigo_modulo,
    ];
    const encontrado = candidatos.find((v) => typeof v === 'string' && v.trim() !== '');
    if (encontrado) return normalizarCodigoModulo(encontrado);

    const nombreRaw = String(adjunto?.nombre_archivo || '').toUpperCase();
    const matchFaseArchivo = nombreRaw.match(/F(\d+)[_\-.]?(\d{1,3})/);
    if (matchFaseArchivo) {
        return normalizarCodigoModulo(`F${matchFaseArchivo[1]}_${matchFaseArchivo[2]}`);
    }
    const matchTransversalArchivo = nombreRaw.match(/T[_\-.]?(\d+)/);
    if (matchTransversalArchivo) {
        return normalizarCodigoModulo(`T_${matchTransversalArchivo[1]}`);
    }

    const nombreArchivo = (adjunto?.nombre_archivo || '').toLowerCase();
    const nombreLimpio = nombreArchivo
        .replace(/_\d{8}_\d{6}\.pdf$/i, '')
        .replace(/\.pdf$/i, '')
        .trim();
    const nombreNormalizado = normalizarTextoBusqueda(nombreLimpio);

    for (const [titulo, codigo] of Object.entries(MAPEO_TITULOS_CODIGO)) {
        const tituloNormalizado = normalizarTextoBusqueda(titulo);
        if (nombreNormalizado.includes(tituloNormalizado)) {
            return normalizarCodigoModulo(codigo);
        }
    }

    // Fallback robusto para títulos variables/largos de transversales
    const tiene = (txt) => nombreNormalizado.includes(txt);
    if (tiene('acta') && tiene('archivo') && tiene('definitivo')) return normalizarCodigoModulo('T_1');
    if (tiene('acta') && tiene('archivo') && tiene('provisional')) return normalizarCodigoModulo('T_2');
    if (tiene('psicoeducacion')) return normalizarCodigoModulo('T_4');
    if (tiene('comunicacion') && tiene('cierre') && tiene('ruta')) return normalizarCodigoModulo('T_5');

    const esDesistimiento =
        tiene('desistim') ||
        tiene('desistimiento') ||
        tiene('desistimieto');
    if (esDesistimiento && (tiene('ruta') || tiene('violencias basadas en genero') || tiene('vbg'))) {
        return normalizarCodigoModulo('T_3');
    }

    return null;
};


export const extraerFaseAdjunto = (adjunto = {}, moduloCodigo = null) => {
    const faseObj = adjunto?.fase || adjunto?.sesion || adjunto?.principal;
    if (faseObj && typeof faseObj === 'object') {
        const codigo = faseObj.valora || faseObj.valorc || faseObj.valor || null;
        return codigo;
    }
    if (adjunto?.fase_codigo) return adjunto.fase_codigo;
    if (adjunto?.codigo_fase) return adjunto.codigo_fase;
    const esTransversal = moduloCodigo && /^T_\d+$/.test(moduloCodigo);
    const nombreArchivo = (adjunto?.nombre_archivo || '').toLowerCase();
    if (esTransversal) {
        const matchFase = nombreArchivo.match(/fase\s*(\d+)/i);
        if (matchFase) return matchFase[1];
    }
    if (moduloCodigo && /^F(\d)_\d{3}$/i.test(moduloCodigo)) {
        const match = moduloCodigo.match(/^F(\d)_\d{3}$/i);
        return match[1];
    }
    return null;
};


export const construirCompletadosPorFase = (adjuntos = []) => {
    const resultado = {};
    (Array.isArray(adjuntos) ? adjuntos : []).forEach((adj) => {
        const moduloCodigo = extraerModuloAdjunto(adj);
        const faseCodigo = extraerFaseAdjunto(adj, moduloCodigo);
        if (!moduloCodigo || !faseCodigo) return;
        if (!resultado[faseCodigo]) resultado[faseCodigo] = {};
        resultado[faseCodigo][moduloCodigo] = true;
    });
    return resultado;
};


export const esAdjuntoFichaPorNombre = (adjunto = {}, ficha = {}) => {
    const nombreFicha = ficha?.secundario?.nombre || '';
    if (!nombreFicha) return false;
    const nombreFichaNorm = normalizarTextoBusqueda(nombreFicha);
    if (!nombreFichaNorm) return false;
    const archivoRaw = String(adjunto?.nombre_archivo || '');
    const sinMarcaBorrador = archivoRaw.replace(/_BORRADOR\.pdf$/i, '.pdf');
    const nombreLimpio = sinMarcaBorrador
        .replace(/_\d{8}_\d{6}\.pdf$/i, '')
        .replace(/\.pdf$/i, '')
        .trim();
    const archivoNorm = normalizarTextoBusqueda(nombreLimpio);
    if (!archivoNorm) return false;
    return archivoNorm.includes(nombreFichaNorm) || nombreFichaNorm.includes(archivoNorm);
};


export const obtenerNombrePersona = (persona = {}) => {
    if (!persona || typeof persona !== 'object') return '';
    const nombres = [
        persona.primer_nombre,
        persona.segundo_nombre,
        persona.primer_apellido,
        persona.segundo_apellido,
    ]
        .map((v) => String(v || '').trim())
        .filter(Boolean);
    if (nombres.length > 0) return nombres.join(' ');
    return String(
        persona.nombre_completo ||
        persona.nombre ||
        persona.username ||
        persona.correo ||
        ''
    ).trim();
};


export const obtenerUltimoAdjuntoFicha = (ficha = {}) => {
    const toTs = (valor) => {
        const ts = new Date(valor || 0).getTime();
        return Number.isFinite(ts) ? ts : 0;
    };
    const adjuntosFicha = (ficha.adjuntos || [])
        .filter((adj) => adj.tipo_adjunto === 1 && adj.archivo && esAdjuntoFichaPorNombre(adj, ficha))
        .sort((a, b) => {
            const fechaB = toTs(b.fecha_modificacion || b.fecha_actualizacion || b.fecha_registro);
            const fechaA = toTs(a.fecha_modificacion || a.fecha_actualizacion || a.fecha_registro);
            return fechaB - fechaA;
        });
    return adjuntosFicha[0] || null;
};


export const extraerFechaRegistro = (obj = {}) => {
    if (!obj || typeof obj !== 'object') return null;
    return (
        obj.fecha_ultimo_guardado ||
        obj.updated_at ||
        obj.fecha_actualizacion ||
        obj.fecha_modificacion ||
        obj.fecha_guardado ||
        obj.fecha_registro ||
        obj.created_at ||
        null
    );
};


export const obtenerUltimoEventoFicha = (ficha = {}) => {
    const toTs = (valor) => {
        const ts = new Date(valor || 0).getTime();
        return Number.isFinite(ts) ? ts : 0;
    };
    const ultimoAdjunto = obtenerUltimoAdjuntoFicha(ficha);
    const progreso = ficha?.progreso || null;
    const fechaAdjunto = extraerFechaRegistro(ultimoAdjunto);
    const fechaProgreso = extraerFechaRegistro(progreso);
    const tsAdjunto = toTs(fechaAdjunto);
    const tsProgreso = toTs(fechaProgreso);
    if (tsProgreso >= tsAdjunto && tsProgreso > 0) {
        return { fuente: 'progreso', data: progreso };
    }
    if (ultimoAdjunto) {
        return { fuente: 'adjunto', data: ultimoAdjunto };
    }
    if (progreso) {
        return { fuente: 'progreso', data: progreso };
    }
    return null;
};


export const obtenerFechaUltimaFicha = (ficha = {}) => {
    const evento = obtenerUltimoEventoFicha(ficha);
    return extraerFechaRegistro(evento?.data);
};


/**
 * Obtiene el nombre del último editor de la ficha.
 * @param {Object} ficha
 * @param {Object|null} usuarioActual usuario actualmente autenticado (this.props.usuario)
 *                                    para resolver `Usuario #id` cuando coincide con el id devuelto.
 */
export const obtenerUltimoEditorFicha = (ficha = {}, usuarioActual = null) => {
    const evento = obtenerUltimoEventoFicha(ficha);
    const origen = evento?.data;
    if (!origen) return '';
    const usuario =
        origen?.usuario_guardado ||
        origen?.usuario_guardado_detalle ||
        origen?.usuario_modificacion ||
        origen?.usuario_actualizacion ||
        origen?.usuario_registro ||
        origen?.usuario ||
        origen?.persona ||
        null;
    const nombreUsuario = obtenerNombrePersona(usuario);
    if (nombreUsuario) return nombreUsuario;
    const nombrePlano = String(
        origen?.usuario_modificacion_nombre ||
        origen?.usuario_actualizacion_nombre ||
        origen?.usuario_guardado_nombre ||
        origen?.usuario_registro_nombre ||
        origen?.creado_por ||
        origen?.updated_by ||
        ''
    ).trim();
    if (nombrePlano) return nombrePlano;
    const updatedById = origen?.updated_by_id || origen?.usuario_registro_id || null;
    if (updatedById !== null && updatedById !== undefined && String(updatedById).trim() !== '') {
        if (usuarioActual?.id != null && String(usuarioActual.id) === String(updatedById)) {
            const nombreUsuarioActual = obtenerNombrePersona(usuarioActual);
            if (nombreUsuarioActual) return nombreUsuarioActual;
        }
        return `Usuario #${updatedById}`;
    }
    return '';
};


export const obtenerEstadoVisualFicha = (ficha = {}) => {
    const ultimoPdf = obtenerUltimoAdjuntoFicha(ficha);
    if (!ultimoPdf?.archivo) {
        return { estado: null, ultimoPdf: null };
    }
    const estadoProgreso = String(ficha?.progreso?.estado || '').trim().toLowerCase();
    const progresoFinalizado = estadoProgreso === 'completed' || estadoProgreso === 'closed';
    if (estadoProgreso === 'pending') {
        return { estado: 'CAMBIOS_PENDIENTES', ultimoPdf };
    }
    const esBorradorPdf = esNombreAdjuntoBorradorFicha(ultimoPdf.nombre_archivo);
    if (esBorradorPdf) {
        return { estado: 'BORRADOR', ultimoPdf };
    }
    const fechaPdf = extraerFechaRegistro(ultimoPdf);
    const fechaProgreso = extraerFechaRegistro(ficha?.progreso || null);
    const tsPdf = fechaPdf ? new Date(fechaPdf).getTime() : 0;
    const tsProgreso = fechaProgreso ? new Date(fechaProgreso).getTime() : 0;
    if (tsPdf > 0 && tsProgreso > tsPdf && !progresoFinalizado) {
        return { estado: 'CAMBIOS_PENDIENTES', ultimoPdf };
    }
    return { estado: 'FINALIZADO', ultimoPdf };
};


export const obtenerMensajeEstadoFicha = (estado) => {
    if (estado === 'CAMBIOS_PENDIENTES') {
        return 'Este documento fue modificado después de ser finalizado. Debes regenerarlo.';
    }
    return '';
};


export const obtenerAutorAdjunto = (adjunto = {}) => {
    const usuario =
        adjunto?.usuario_modificacion ||
        adjunto?.usuario_actualizacion ||
        adjunto?.usuario_registro ||
        adjunto?.usuario ||
        adjunto?.persona ||
        null;
    const nombreUsuario = obtenerNombrePersona(usuario);
    if (nombreUsuario) return nombreUsuario;
    return String(
        adjunto?.usuario_modificacion_nombre ||
        adjunto?.usuario_actualizacion_nombre ||
        adjunto?.usuario_registro_nombre ||
        adjunto?.creado_por ||
        adjunto?.updated_by ||
        ''
    ).trim();
};


export const normalizarCodigoFicha = (valor) => {
    if (typeof valor !== 'string' && typeof valor !== 'number') return '';
    const limpio = String(valor).trim();
    if (!limpio) return '';
    const lower = limpio.toLowerCase();
    if (lower === 'null' || lower === 'undefined' || lower === 'nan') return '';
    return limpio;
};


/**
 * Determina el código de tipo de servicio que debe usarse al consultar el progreso.
 * @param {Object} ficha
 * @param {string} tipoServicioActualCodigo fallback (state.tipoServicioActual?.codigo)
 */
export const obtenerTipoServicioCodigoParaProgreso = (ficha = {}, tipoServicioActualCodigo = '') => {
    const codigoServicio = ficha?.principal?.codigo || tipoServicioActualCodigo || '';
    const codigoFichaSecundario = ficha?.secundario?.codigo || '';
    if (codigoServicio === COD_SOCIOEMOCIONAL && codigoFichaSecundario === COD_FICHA_UNICA) {
        return COD_FICHA_UNICA;
    }
    return codigoServicio;
};


export const normalizarRespuestaProgreso = (resp, codigoFichaSecundario = '') => {
    const lista = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.results)
            ? resp.results
            : null;
    if (!lista) {
        return (resp && typeof resp === 'object') ? resp : null;
    }
    const codigoObjetivo = normalizarCodigoFicha(codigoFichaSecundario);
    const matchDirecto = lista.find((item) => normalizarCodigoFicha(item?.codigo_ficha_secundario) === codigoObjetivo);
    if (matchDirecto) return matchDirecto;
    const matchNested = lista.find((item) => normalizarCodigoFicha(item?.ficha?.secundario?.codigo) === codigoObjetivo);
    if (matchNested) return matchNested;
    const ordenados = [...lista].sort((a, b) => {
        const fb = new Date(extraerFechaRegistro(b) || 0).getTime();
        const fa = new Date(extraerFechaRegistro(a) || 0).getTime();
        return fb - fa;
    });
    return ordenados[0] || null;
};


export const normalizarPayloadProgreso = (payload) => {
    if (!payload || typeof payload !== 'object') return null;
    const candidato =
        payload?.progreso ||
        payload?.data?.progreso ||
        payload?.data ||
        payload;
    return (candidato && typeof candidato === 'object') ? candidato : null;
};


export const esRespuestaProgresoValida = (progreso) => Boolean(
    progreso && typeof progreso === 'object' && (
        progreso.existe === true ||
        progreso.fecha_ultimo_guardado ||
        progreso.updated_at ||
        progreso.usuario_guardado ||
        progreso.usuario_guardado_nombre ||
        progreso.updated_by_id ||
        progreso.estado ||
        progreso.datos
    )
);
